/**
 * API service for admin dashboard
 * Handles authenticated requests to the admin API
 */

import { auth } from '../firebase-config';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., '/api/stats/pipeline')
 * @param {object} options - Fetch options
 * @returns {Promise<object>} Response data
 */
export async function makeAuthenticatedRequest(endpoint, options = {}) {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    const err = new Error(error.error || error.message || 'Request failed');
    err.status = response.status;
    err.body = error; // full payload (e.g. coverage details on a 422)
    throw err;
  }

  // 204 No Content (e.g. domain delete) has an empty body; parsing it would throw
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

// Pipeline API
export const pipelineApi = {
  triggerPhase: (phase, filters = {}) =>
    makeAuthenticatedRequest(`/api/pipeline/trigger/${phase}`, {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerAnalysis: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/analysis', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerApplyMappings: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/apply-mappings', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerEmbeddings: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/embeddings', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerNeo4j: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/neo4j', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  getJobs: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/pipeline/jobs?${searchParams}`);
  },

  getJob: (jobId) =>
    makeAuthenticatedRequest(`/api/pipeline/jobs/${jobId}`),

  getCounts: (phase, params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/pipeline/counts/${phase}?${searchParams}`);
  },

  triggerRefreshMappings: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/refresh-mappings', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerAINormalization: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/ai-normalization', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerImageAnalysis: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/image-analysis', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerNeo4jEnhanced: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/neo4j-enhanced', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerGtinSync: (filters = {}) =>
    makeAuthenticatedRequest('/api/gtin-sync', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  getPendingCategoryFamilyResync: () =>
    makeAuthenticatedRequest('/api/pipeline/category-family-resync/pending'),

  triggerCategoryFamilyResync: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/category-family-resync', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),

  triggerSupplierCodes: (filters = {}) =>
    makeAuthenticatedRequest('/api/pipeline/trigger/supplier-codes', {
      method: 'POST',
      body: JSON.stringify(filters),
    }),
};

// Mapping API
export const mappingApi = {
  getReviews: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/mappings/reviews?${searchParams}`);
  },

  getReview: (reviewId) =>
    makeAuthenticatedRequest(`/api/mappings/reviews/${reviewId}`),

  getConflictDetails: (reviewId) =>
    makeAuthenticatedRequest(`/api/mappings/reviews/${reviewId}/conflict`),

  batchUpdate: (updates) =>
    makeAuthenticatedRequest('/api/mappings/reviews/batch', {
      method: 'POST',
      body: JSON.stringify({ updates }),
    }),

  getStatusCounts: () =>
    makeAuthenticatedRequest('/api/mappings/status-counts'),

  getBrandsWithPending: () =>
    makeAuthenticatedRequest('/api/mappings/brands'),

  getNormalizedValues: (attributeType, params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/mappings/normalized-values/${attributeType}?${searchParams}`);
  },

  getFamilies: (attributeType) =>
    makeAuthenticatedRequest(`/api/mappings/families/${attributeType}`),

  getExistingMappings: (attributeType, params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/mappings/existing/${attributeType}?${searchParams}`);
  },

  createMapping: (attributeType, rawValue, normalizedValue, brand = null, family = null) =>
    makeAuthenticatedRequest('/api/mappings/create', {
      method: 'POST',
      body: JSON.stringify({
        attribute_type: attributeType,
        raw_value: rawValue,
        normalized_value: normalizedValue,
        brand,
        family,
      }),
    }),

  updateMapping: (attributeType, mappingId, rawValue, newNormalizedValue) =>
    makeAuthenticatedRequest(`/api/mappings/existing/${attributeType}/${mappingId}`, {
      method: 'PUT',
      body: JSON.stringify({
        raw_value: rawValue,
        new_normalized_value: newNormalizedValue,
      }),
    }),

  updateMappingFamily: (attributeType, mappingId, family) =>
    makeAuthenticatedRequest(`/api/mappings/existing/${attributeType}/${mappingId}/family`, {
      method: 'PUT',
      body: JSON.stringify({ family }),
    }),

  // Generic Values
  getGenericValues: (attributeType = null) => {
    const params = attributeType ? { attribute_type: attributeType } : {};
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/mappings/generic-values?${searchParams}`);
  },

  getGenericValueAffectedCount: (rawValue, attributeType) => {
    const searchParams = new URLSearchParams({
      raw_value: rawValue,
      attribute_type: attributeType,
    });
    return makeAuthenticatedRequest(`/api/mappings/generic-values/affected-count?${searchParams}`);
  },

  addGenericValue: (rawValue, attributeType, clearExisting = false) =>
    makeAuthenticatedRequest('/api/mappings/generic-values', {
      method: 'POST',
      body: JSON.stringify({
        raw_value: rawValue,
        attribute_type: attributeType,
        clear_existing: clearExisting,
      }),
    }),

  deleteGenericValue: (genericValueId) =>
    makeAuthenticatedRequest(`/api/mappings/generic-values/${genericValueId}`, {
      method: 'DELETE',
    }),
};

// Stats API
export const statsApi = {
  getPipelineStats: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/stats/pipeline?${searchParams}`);
  },

  getRetailers: () =>
    makeAuthenticatedRequest('/api/stats/retailers').then(d => d?.retailers ?? d ?? []),

  getBrands: (retailerId = null) => {
    const params = retailerId ? { retailer_id: retailerId } : {};
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/stats/brands?${searchParams}`).then(d => d?.brands ?? d ?? []);
  },

  getAttributeTypeStats: () =>
    makeAuthenticatedRequest('/api/stats/attribute-types'),

  clearCache: () =>
    makeAuthenticatedRequest('/api/stats/cache/clear', {
      method: 'POST',
    }),

  // Retailer Relationship Analytics
  getAllRetailerPairs: (options = {}) => {
    const searchParams = new URLSearchParams();
    if (options.relationship_type) {
      searchParams.append('relationship_type', options.relationship_type);
    }
    const queryString = searchParams.toString();
    return makeAuthenticatedRequest(`/api/stats/retailer-pairs${queryString ? `?${queryString}` : ''}`);
  },

  getRetailerPairStats: (retailerA, retailerB, options = {}) => {
    const searchParams = new URLSearchParams();
    if (options.include_products !== undefined) {
      searchParams.append('include_products', options.include_products);
    }
    if (options.limit) {
      searchParams.append('limit', options.limit);
    }
    if (options.relationship_type) {
      searchParams.append('relationship_type', options.relationship_type);
    }
    const queryString = searchParams.toString();
    return makeAuthenticatedRequest(
      `/api/stats/retailer-pairs/${encodeURIComponent(retailerA)}/${encodeURIComponent(retailerB)}${queryString ? `?${queryString}` : ''}`
    );
  },

  getRetailerPairProducts: (retailerA, retailerB, options = {}) => {
    const searchParams = new URLSearchParams();
    if (options.search) {
      searchParams.append('search', options.search);
    }
    if (options.limit) {
      searchParams.append('limit', options.limit);
    }
    if (options.offset !== undefined) {
      searchParams.append('offset', options.offset);
    }
    if (options.relationship_type) {
      searchParams.append('relationship_type', options.relationship_type);
    }
    if (options.match_type) {
      searchParams.append('match_type', options.match_type);
    }
    const queryString = searchParams.toString();
    return makeAuthenticatedRequest(
      `/api/stats/retailer-pairs/${encodeURIComponent(retailerA)}/${encodeURIComponent(retailerB)}/products${queryString ? `?${queryString}` : ''}`
    );
  },

  getRetailerPairHistory: (retailerA, retailerB, options = {}) => {
    const searchParams = new URLSearchParams();
    if (options.days) {
      searchParams.append('days', options.days);
    }
    if (options.relationship_type) {
      searchParams.append('relationship_type', options.relationship_type);
    }
    const queryString = searchParams.toString();
    return makeAuthenticatedRequest(
      `/api/stats/retailer-pairs/${encodeURIComponent(retailerA)}/${encodeURIComponent(retailerB)}/history${queryString ? `?${queryString}` : ''}`
    );
  },

  getProductMatches: (retailerId, options = {}) => {
    const searchParams = new URLSearchParams();
    if (options.raw_product_id) {
      searchParams.append('raw_product_id', options.raw_product_id);
    }
    if (options.product_name) {
      searchParams.append('product_name', options.product_name);
    }
    if (options.normalized_brand) {
      searchParams.append('normalized_brand', options.normalized_brand);
    }
    if (options.raw_color) {
      searchParams.append('raw_color', options.raw_color);
    }
    if (options.raw_brand) {
      searchParams.append('raw_brand', options.raw_brand);
    }
    return makeAuthenticatedRequest(
      `/api/stats/product-matches/${encodeURIComponent(retailerId)}?${searchParams}`
    );
  },

  searchProductsUnified: (query, options = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.append('q', query);
    if (options.limit) {
      searchParams.append('limit', options.limit);
    }
    return makeAuthenticatedRequest(`/api/stats/products/search?${searchParams}`);
  },

  // Product Detail (for side panel)
  getProductById: (productId) => {
    return makeAuthenticatedRequest(`/api/stats/product/${encodeURIComponent(productId)}`);
  },

  // False Negatives Explorer
  getFalseNegativeCandidates: (options = {}) => {
    const searchParams = new URLSearchParams();
    if (options.retailer_a) {
      searchParams.append('retailer_a', options.retailer_a);
    }
    if (options.retailer_b) {
      searchParams.append('retailer_b', options.retailer_b);
    }
    if (options.normalized_brand) {
      searchParams.append('normalized_brand', options.normalized_brand);
    }
    if (options.min_similarity !== undefined) {
      searchParams.append('min_similarity', options.min_similarity);
    }
    if (options.limit) {
      searchParams.append('limit', options.limit);
    }
    if (options.offset !== undefined) {
      searchParams.append('offset', options.offset);
    }
    if (options.search) {
      searchParams.append('search', options.search);
    }
    return makeAuthenticatedRequest(`/api/stats/false-negative-candidates?${searchParams}`);
  },

  getBrandsForRetailers: (retailerA, retailerB) => {
    const searchParams = new URLSearchParams();
    searchParams.append('retailer_a', retailerA);
    searchParams.append('retailer_b', retailerB);
    return makeAuthenticatedRequest(`/api/stats/brands-for-retailers?${searchParams}`);
  },
};

// Auth API
export const authApi = {
  getCurrentUser: () =>
    makeAuthenticatedRequest('/api/auth/me'),
};

// Metrics API (Phase 1.5 Pipeline Metrics Dashboard)
export const metricsApi = {
  getCurrentMetrics: (params = {}) => {
    const searchParams = new URLSearchParams();
    // Only add non-empty values to avoid passing empty strings
    if (params.retailer_id) searchParams.append('retailer_id', params.retailer_id);
    if (params.normalized_brand) searchParams.append('normalized_brand', params.normalized_brand);
    return makeAuthenticatedRequest(`/api/admin/v1/metrics/current?${searchParams}`);
  },

  getHistory: (params = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.append('days', params.days || 30);
    if (params.retailer_id) searchParams.append('retailer_id', params.retailer_id);
    if (params.normalized_brand) searchParams.append('normalized_brand', params.normalized_brand);
    return makeAuthenticatedRequest(`/api/admin/v1/metrics/history?${searchParams}`);
  },

  captureSnapshot: (options = {}) =>
    makeAuthenticatedRequest('/api/admin/v1/metrics/snapshot', {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  getRetailerSnapshots: (date = null) => {
    const params = date ? `?date=${date}` : '';
    return makeAuthenticatedRequest(`/api/admin/v1/metrics/retailers${params}`);
  },

  compare: (date1, date2, params = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.append('date1', date1);
    searchParams.append('date2', date2);
    if (params.retailer_id) searchParams.append('retailer_id', params.retailer_id);
    if (params.normalized_brand) searchParams.append('normalized_brand', params.normalized_brand);
    return makeAuthenticatedRequest(`/api/admin/v1/metrics/compare?${searchParams}`);
  },
};

// Commission Management API
export const commissionApi = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.selling_retailer_id) searchParams.append('selling_retailer_id', params.selling_retailer_id);
    if (params.buying_retailer_id) searchParams.append('buying_retailer_id', params.buying_retailer_id);
    if (params.status) searchParams.append('status', params.status);
    if (params.match_level) searchParams.append('match_level', params.match_level);
    if (params.invoiced !== undefined) searchParams.append('invoiced', params.invoiced);
    if (params.has_ai_recommendation !== undefined) searchParams.append('has_ai_recommendation', params.has_ai_recommendation);
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset !== undefined) searchParams.append('offset', params.offset);
    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    if (params.sort_order) searchParams.append('sort_order', params.sort_order);
    return makeAuthenticatedRequest(`/api/admin/v1/commissions?${searchParams}`);
  },

  get: (itemId) =>
    makeAuthenticatedRequest(`/api/admin/v1/commissions/${itemId}`),

  getCookieData: (itemId) =>
    makeAuthenticatedRequest(`/api/admin/v1/commissions/${itemId}/cookie`),

  getOverview: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    return makeAuthenticatedRequest(`/api/admin/v1/commissions/overview?${searchParams}`);
  },

  getSummary: () =>
    makeAuthenticatedRequest('/api/admin/v1/commissions/summary'),

  getRetailers: () =>
    makeAuthenticatedRequest('/api/admin/v1/commissions/retailers'),

  approve: (itemId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/commissions/${itemId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  decline: (itemId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/commissions/${itemId}/decline`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// AI Mapping API (for AI-extracted category/pattern normalization)
export const aiMappingApi = {
  // Get unmapped categories from image/description extraction
  getUnmappedCategories: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/admin/unmapped-categories?${searchParams}`);
  },

  // Get unmapped patterns from image extraction
  getUnmappedPatterns: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/admin/unmapped-patterns?${searchParams}`);
  },

  // Get stats for unmapped values (for dashboard widget)
  getUnmappedStats: () =>
    makeAuthenticatedRequest('/api/admin/unmapped-stats'),

  // Get category families for dropdown
  getCategoryFamilies: () =>
    makeAuthenticatedRequest('/api/admin/ai-category-families'),

  // Get pattern families for dropdown
  getPatternFamilies: () =>
    makeAuthenticatedRequest('/api/admin/ai-pattern-families'),

  // Get existing AI category mappings
  getCategoryMappings: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/admin/ai-category-mappings?${searchParams}`);
  },

  // Get existing AI pattern mappings
  getPatternMappings: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/admin/ai-pattern-mappings?${searchParams}`);
  },

  // Map an extracted value to an EXISTING category type (closed list; the type's family follows)
  createCategoryMapping: (extractedValue, normalizedCategory) =>
    makeAuthenticatedRequest('/api/admin/ai-category-mappings', {
      method: 'POST',
      body: JSON.stringify({
        extracted_value: extractedValue,
        normalized_category: normalizedCategory,
      }),
    }),

  // Map an extracted value to an EXISTING pattern type (closed list; the type's family follows)
  createPatternMapping: (extractedValue, normalizedPattern) =>
    makeAuthenticatedRequest('/api/admin/ai-pattern-mappings', {
      method: 'POST',
      body: JSON.stringify({
        extracted_value: extractedValue,
        normalized_pattern: normalizedPattern,
      }),
    }),
};

// Code Extraction API
export const codeExtractionApi = {
  // Brand Rules
  getBrandRules: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.normalized_brand) searchParams.append('normalized_brand', params.normalized_brand);
    return makeAuthenticatedRequest(`/api/code-extraction/brand-rules?${searchParams}`);
  },
  createBrandRule: (data) =>
    makeAuthenticatedRequest('/api/code-extraction/brand-rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateBrandRule: (ruleId, data) =>
    makeAuthenticatedRequest(`/api/code-extraction/brand-rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Retailer Config
  getRetailerConfig: (retailerId) =>
    makeAuthenticatedRequest(`/api/code-extraction/retailer-config/${encodeURIComponent(retailerId)}`),
  updateRetailerConfig: (retailerId, data) =>
    makeAuthenticatedRequest(`/api/code-extraction/retailer-config/${encodeURIComponent(retailerId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  unlockMatching: (retailerId) =>
    makeAuthenticatedRequest('/api/code-extraction/unlock-matching', {
      method: 'POST',
      body: JSON.stringify({ retailer_id: retailerId }),
    }),

  getRetailerCoverage: (retailerId) =>
    makeAuthenticatedRequest(`/api/code-extraction/retailer-coverage/${encodeURIComponent(retailerId)}`),

  // B1: re-extract + re-sync a brand (after a manual rule edit) to apply it
  reextractResync: ({ normalizedBrand, retailerId } = {}) =>
    makeAuthenticatedRequest('/api/code-extraction/reextract-resync', {
      method: 'POST',
      body: JSON.stringify({ normalized_brand: normalizedBrand, retailer_id: retailerId }),
    }),

  // Self-healing re-inference (A1.3)
  getBrittleBrands: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.min_failed != null) searchParams.append('min_failed', params.min_failed);
    if (params.max_extract_rate != null) searchParams.append('max_extract_rate', params.max_extract_rate);
    return makeAuthenticatedRequest(`/api/code-extraction/brittle-brands?${searchParams}`);
  },
  reinferBrittle: (data = {}) =>
    makeAuthenticatedRequest('/api/code-extraction/reinfer-brittle', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  reinferStatus: (jobId) =>
    makeAuthenticatedRequest(`/api/code-extraction/reinfer-status/${jobId}`),
  getFailedBreakdown: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.retailer_id) searchParams.append('retailer_id', params.retailer_id);
    return makeAuthenticatedRequest(`/api/code-extraction/failed-breakdown?${searchParams}`);
  },

  // AI Discovery
  discoverBrandFormat: (data) =>
    makeAuthenticatedRequest('/api/code-extraction/discover-brand-format', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  discoverBrandFormatBatch: (data) =>
    makeAuthenticatedRequest('/api/code-extraction/discover-brand-format-batch', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  brandFormatFromExamples: (data) =>
    makeAuthenticatedRequest('/api/code-extraction/brand-format-from-examples', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  testPattern: (data) =>
    makeAuthenticatedRequest('/api/code-extraction/test-pattern', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Stats
  getStats: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.retailer_id) searchParams.append('retailer_id', params.retailer_id);
    return makeAuthenticatedRequest(`/api/code-extraction/stats?${searchParams}`);
  },
};

// Brand Mapping API
export const brandApi = {
  // Unified list: every raw brand with its mapping state (replaces the two tabs)
  getAllBrands: (params = {}) => {
    const searchParams = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
    );
    return makeAuthenticatedRequest(`/api/brands/all?${searchParams}`);
  },

  getOrphanBrands: () => makeAuthenticatedRequest('/api/brands/orphans'),

  backfillOrphanBrands: (rawBrands) =>
    makeAuthenticatedRequest('/api/brands/orphans/backfill', {
      method: 'POST',
      body: JSON.stringify(rawBrands ? { raw_brands: rawBrands } : {}),
    }),

  getNormalizedBrands: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return makeAuthenticatedRequest(`/api/brands/normalized?${searchParams}`);
  },

  createMapping: (rawBrand, normalizedBrand) =>
    makeAuthenticatedRequest('/api/brands/mapping', {
      method: 'POST',
      body: JSON.stringify({ raw_brand: rawBrand, normalized_brand: normalizedBrand }),
    }),

  batchCreateMappings: (mappings) =>
    makeAuthenticatedRequest('/api/brands/mapping/batch', {
      method: 'POST',
      body: JSON.stringify({ mappings }),
    }),

  updateMapping: (mappingId, rawValue, newNormalizedValue) =>
    makeAuthenticatedRequest(`/api/brands/mappings/${mappingId}`, {
      method: 'PUT',
      body: JSON.stringify({
        raw_value: rawValue,
        new_normalized_value: newNormalizedValue,
      }),
    }),
};

// Billing entities: the legal entity behind one or more storefronts. Every invoice,
// self-bill and settlement is keyed on these, never on a retailer_id.
export const billingEntityApi = {
  list: () =>
    makeAuthenticatedRequest('/api/admin/v1/billing-entities'),

  get: (entityId) =>
    makeAuthenticatedRequest(`/api/admin/v1/billing-entities/${encodeURIComponent(entityId)}`),

  // data: entity fields, plus optional storefront_ids to link atomically on create.
  create: (data) =>
    makeAuthenticatedRequest('/api/admin/v1/billing-entities', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (entityId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/billing-entities/${encodeURIComponent(entityId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// Retailer Management API
export const retailerApi = {
  list: () =>
    makeAuthenticatedRequest('/api/admin/v1/retailers'),

  getDetail: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}`),

  update: (retailerId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  setOverride: (retailerId, disabled, reason = '') =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/override`, {
      method: 'POST',
      body: JSON.stringify({ disabled, reason }),
    }),

  listDomains: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/domains`),

  // `extra` carries the storefront language facts (language, localized_paths) — migration 228.
  addDomain: (retailerId, domain, domainType, extra = {}) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/domains`, {
      method: 'POST',
      body: JSON.stringify({ domain, domain_type: domainType, ...extra }),
    }),

  // language and/or localized_paths of one storefront/alias row.
  updateDomain: (retailerId, domain, fields) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/domains/${encodeURIComponent(domain)}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }),

  deleteDomain: (retailerId, domain) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/domains/${encodeURIComponent(domain)}`, {
      method: 'DELETE',
    }),

  setCanonicalDomain: (retailerId, domain) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/domains/${encodeURIComponent(domain)}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_canonical: true }),
    }),

  // Stock-veto pre-flight: run the live ground-truth check on demand (Phase 4). Regime-aware:
  // channable -> DOM crowd pre-flight; shopware/shopify -> live get_stock spot-check.
  checkReadiness: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/readiness-check`, {
      method: 'POST',
    }),

  // Regime-aware stock-veto gate status (read-only): channable crowd gates vs live_api API health.
  stockGateStatus: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/stock-gate-status`),

  // Clear an auto-suspension (deliberate admin action).
  clearSuspension: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/clear-suspension`, {
      method: 'POST',
    }),

  // Currently-active crowdsourced stock vetoes, per retailer (network view).
  activeVetoes: () =>
    makeAuthenticatedRequest('/api/admin/v1/stock-observations/active-vetoes'),

  // Drill-down: the individual vetoed sizes for one retailer.
  activeVetoesForRetailer: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/stock-observations/active-vetoes?retailer_id=${encodeURIComponent(retailerId)}`),

  // Clear a retailer's crowd-veto auto-suspension (deliberate admin action).
  clearStockSuspension: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/clear-suspension`, {
      method: 'POST',
    }),
};

// Performance Metrics API
export const orderShareApi = {
  // Estimated share of each retailer's WEBSHOP orders that came from HandOff.
  // Internal only — never surfaced to retailers.
  getSummary: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    if (params.trend_months) searchParams.append('trend_months', params.trend_months);
    return makeAuthenticatedRequest(`/api/admin/v1/order-share/summary?${searchParams}`);
  },
};

export const performanceApi = {
  getSummary: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    if (params.retailer_ids?.length) {
      params.retailer_ids.forEach(id => searchParams.append('retailer_id', id));
    }
    return makeAuthenticatedRequest(`/api/admin/v1/performance/summary?${searchParams}`);
  },

  getTrends: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.from_date) searchParams.append('from_date', params.from_date);
    if (params.to_date) searchParams.append('to_date', params.to_date);
    if (params.granularity) searchParams.append('granularity', params.granularity);
    if (params.retailer_ids?.length) {
      params.retailer_ids.forEach(id => searchParams.append('retailer_id', id));
    }
    return makeAuthenticatedRequest(`/api/admin/v1/performance/trends?${searchParams}`);
  },

  getRetailerPartners: (retailerId, { from_date, to_date } = {}) => {
    const p = new URLSearchParams();
    if (from_date) p.append('from_date', from_date);
    if (to_date) p.append('to_date', to_date);
    return makeAuthenticatedRequest(`/api/admin/v1/performance/retailer/${retailerId}/partners?${p}`);
  },
};

// Click-to-Order Performance API
export const clickPerformanceApi = {
  getSummary: ({ from_date, to_date, retailer_ids = [] } = {}) => {
    const p = new URLSearchParams();
    if (from_date) p.append('from_date', from_date);
    if (to_date) p.append('to_date', to_date);
    retailer_ids.forEach(id => p.append('retailer_id', id));
    return makeAuthenticatedRequest(`/api/admin/v1/click-performance/summary?${p}`);
  },

  getRetailerPartners: (retailerId, { from_date, to_date } = {}) => {
    const p = new URLSearchParams();
    if (from_date) p.append('from_date', from_date);
    if (to_date) p.append('to_date', to_date);
    return makeAuthenticatedRequest(`/api/admin/v1/click-performance/retailer/${retailerId}/partners?${p}`);
  },

  getTrends: ({ from_date, to_date, retailer_id, direction = 'both' } = {}) => {
    const p = new URLSearchParams();
    if (from_date) p.append('from_date', from_date);
    if (to_date) p.append('to_date', to_date);
    if (retailer_id) p.append('retailer_id', retailer_id);
    p.append('direction', direction);
    return makeAuthenticatedRequest(`/api/admin/v1/click-performance/trends?${p}`);
  },
};

// Badge Fairness API (spec: badge-fairness.md — balances, per-arm stats, guardrail)
export const badgeFairnessApi = {
  getSummary: () => makeAuthenticatedRequest('/api/admin/v1/badge-fairness/summary'),

  getTrends: ({ days = 90 } = {}) => {
    const p = new URLSearchParams();
    p.append('days', days);
    return makeAuthenticatedRequest(`/api/admin/v1/badge-fairness/trends?${p}`);
  },

  // Balance Control (spec overlay/balance-control.md §3.2, §3.7): the band lives on retailers;
  // the exposure table is the pre-grant check (spec §3.7); the grant itself is audited server-side.
  setBalanceBand: (retailerId, { band_orders }) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/balance-band`, {
      method: 'PATCH',
      body: JSON.stringify({ band_orders }),
    }),

  // side omitted = the edge the retailer's latest net position points to (net < 0 → outbound).
  getExposure: (retailerId, side = null, days = 30) => {
    const p = new URLSearchParams();
    p.append('retailer_id', retailerId);
    if (side) p.append('side', side);
    p.append('days', days);
    return makeAuthenticatedRequest(`/api/admin/v1/balance-control/exposure?${p}`);
  },
};

// Matches & Impressions API (network funnel monitoring + revenue projection)
export const saleFilterCounterfactualApi = {
  // Per-retailer counterfactual buckets + valuation (same model as Badge Fairness Adj. NGS).
  getNetwork: ({ from_date, to_date } = {}) => {
    const p = new URLSearchParams();
    if (from_date) p.append('from_date', from_date);
    if (to_date) p.append('to_date', to_date);
    return makeAuthenticatedRequest(`/api/admin/v1/sale-filter-counterfactual?${p}`);
  },
};

export const matchesImpressionsApi = {
  getSummary: ({ from_date, to_date, retailer_ids = [] } = {}) => {
    const p = new URLSearchParams();
    if (from_date) p.append('from_date', from_date);
    if (to_date) p.append('to_date', to_date);
    retailer_ids.forEach(id => p.append('retailer_id', id));
    return makeAuthenticatedRequest(`/api/admin/v1/matches-impressions/summary?${p}`);
  },

  getProjection: ({ from_date, to_date, new_retailer_catalogue_size, new_retailer_brand_overlap } = {}) => {
    const p = new URLSearchParams();
    if (from_date) p.append('from_date', from_date);
    if (to_date) p.append('to_date', to_date);
    if (new_retailer_catalogue_size != null) p.append('new_retailer_catalogue_size', new_retailer_catalogue_size);
    if (new_retailer_brand_overlap != null) p.append('new_retailer_brand_overlap', new_retailer_brand_overlap);
    return makeAuthenticatedRequest(`/api/admin/v1/matches-impressions/projection?${p}`);
  },

  getBrandOverlap: (brands = []) =>
    makeAuthenticatedRequest('/api/admin/v1/matches-impressions/brand-overlap', {
      method: 'POST',
      body: JSON.stringify({ brands }),
    }),

  getPotentialTrend: ({ days = 30 } = {}) =>
    makeAuthenticatedRequest(`/api/admin/v1/matches-impressions/potential-trend?days=${days}`),
};

// Size Alias API
export const sizeAliasApi = {
  list: () =>
    makeAuthenticatedRequest('/api/admin/v1/size-aliases'),

  create: (fromSize, toSize, notes = '') =>
    makeAuthenticatedRequest('/api/admin/v1/size-aliases', {
      method: 'POST',
      body: JSON.stringify({ from_size: fromSize, to_size: toSize, notes }),
    }),

  update: (aliasId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-aliases/${aliasId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (aliasId) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-aliases/${aliasId}`, {
      method: 'DELETE',
    }),

  previewImpact: (fromSize) =>
    makeAuthenticatedRequest('/api/admin/v1/size-aliases/preview-impact', {
      method: 'POST',
      body: JSON.stringify({ from_size: fromSize }),
    }),
};

// Phase 2: Size Schemas (canonical schema vocabulary + applicable family mappings)
export const sizeSchemaApi = {
  list: () =>
    makeAuthenticatedRequest('/api/admin/v1/size-schemas'),

  create: (data) =>
    makeAuthenticatedRequest('/api/admin/v1/size-schemas', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (schemaKey, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-schemas/${schemaKey}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  addFamily: (schemaKey, familyId, priority = 1, notes = null) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-schemas/${schemaKey}/families`, {
      method: 'POST',
      body: JSON.stringify({ family_id: familyId, priority, notes }),
    }),

  updateFamily: (schemaKey, familyId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-schemas/${schemaKey}/families/${familyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  removeFamily: (schemaKey, familyId) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-schemas/${schemaKey}/families/${familyId}`, {
      method: 'DELETE',
    }),

  listCategoryFamilies: () =>
    makeAuthenticatedRequest('/api/admin/v1/category-families'),

  reclassify: () =>
    makeAuthenticatedRequest('/api/admin/v1/size-schema/reclassify', { method: 'POST' }),
};

// Phase 2: Size Curves (ordered size lists per schema, used for size injection ordering)
export const sizeCurveApi = {
  list: (schemaKey) => {
    const qs = schemaKey ? `?schema_key=${encodeURIComponent(schemaKey)}` : '';
    return makeAuthenticatedRequest(`/api/admin/v1/size-curves${qs}`);
  },

  create: (data) =>
    makeAuthenticatedRequest('/api/admin/v1/size-curves', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (curveId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-curves/${curveId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (curveId) =>
    makeAuthenticatedRequest(`/api/admin/v1/size-curves/${curveId}`, {
      method: 'DELETE',
    }),
};

// Color taxonomy (families + normalized colors) — Attribute Mapping → Colors tab
export const colorTaxonomyApi = {
  listFamilies: () =>
    makeAuthenticatedRequest('/api/admin/v1/color-taxonomy/families'),

  createFamily: (data) =>
    makeAuthenticatedRequest('/api/admin/v1/color-taxonomy/families', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFamily: (familyId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/color-taxonomy/families/${familyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteFamily: (familyId) =>
    makeAuthenticatedRequest(`/api/admin/v1/color-taxonomy/families/${familyId}`, {
      method: 'DELETE',
    }),

  listTypes: () =>
    makeAuthenticatedRequest('/api/admin/v1/color-taxonomy/types'),

  updateTypeFamily: (normalizedValue, family) =>
    makeAuthenticatedRequest(
      `/api/admin/v1/color-taxonomy/types/${encodeURIComponent(normalizedValue)}`,
      { method: 'PUT', body: JSON.stringify({ family }) }
    ),
};

// Pattern taxonomy (families + normalized patterns) — Attribute Mapping → Patterns tab
export const patternTaxonomyApi = {
  listFamilies: () =>
    makeAuthenticatedRequest('/api/admin/v1/pattern-taxonomy/families'),

  createFamily: (data) =>
    makeAuthenticatedRequest('/api/admin/v1/pattern-taxonomy/families', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFamily: (familyId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/pattern-taxonomy/families/${familyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteFamily: (familyId) =>
    makeAuthenticatedRequest(`/api/admin/v1/pattern-taxonomy/families/${familyId}`, {
      method: 'DELETE',
    }),

  listTypes: () =>
    makeAuthenticatedRequest('/api/admin/v1/pattern-taxonomy/types'),

  // The only way a pattern type is created (closed list)
  createType: (pattern, family) =>
    makeAuthenticatedRequest('/api/admin/v1/pattern-taxonomy/types', {
      method: 'POST',
      body: JSON.stringify({ pattern, family }),
    }),

  updateTypeFamily: (normalizedPattern, family) =>
    makeAuthenticatedRequest(
      `/api/admin/v1/pattern-taxonomy/types/${encodeURIComponent(normalizedPattern)}`,
      { method: 'PUT', body: JSON.stringify({ family }) }
    ),
};

// Category taxonomy (families + types) — Attribute Mapping → Categories tab
export const categoryTaxonomyApi = {
  listFamilies: () =>
    makeAuthenticatedRequest('/api/admin/v1/category-taxonomy/families'),

  createFamily: (data) =>
    makeAuthenticatedRequest('/api/admin/v1/category-taxonomy/families', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFamily: (familyId, data) =>
    makeAuthenticatedRequest(`/api/admin/v1/category-taxonomy/families/${familyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteFamily: (familyId) =>
    makeAuthenticatedRequest(`/api/admin/v1/category-taxonomy/families/${familyId}`, {
      method: 'DELETE',
    }),

  listTypes: () =>
    makeAuthenticatedRequest('/api/admin/v1/category-taxonomy/types'),

  // The only way a category type is created (closed list)
  createType: (category, familyId) =>
    makeAuthenticatedRequest('/api/admin/v1/category-taxonomy/types', {
      method: 'POST',
      body: JSON.stringify({ category, family_id: familyId }),
    }),

  updateTypeFamily: (category, familyId) =>
    makeAuthenticatedRequest(
      `/api/admin/v1/category-taxonomy/types/${encodeURIComponent(category)}`,
      { method: 'PUT', body: JSON.stringify({ family_id: familyId }) }
    ),
};

// Phase 2: Unknown Sizes (curation queue — read-only aggregations)
export const unknownSizesApi = {
  summary: () =>
    makeAuthenticatedRequest('/api/admin/v1/unknown-sizes/summary'),

  list: (filters = {}) => {
    const params = new URLSearchParams();
    params.set('limit', filters.limit ?? 100);
    if (filters.retailer_id) params.set('retailer_id', filters.retailer_id);
    if (filters.family) params.set('family', filters.family);
    if (filters.raw_size_search) params.set('raw_size_search', filters.raw_size_search);
    return makeAuthenticatedRequest(`/api/admin/v1/unknown-sizes?${params.toString()}`);
  },

  filterOptions: () =>
    makeAuthenticatedRequest('/api/admin/v1/unknown-sizes/filter-options'),

  drilldown: (retailerId, rawSize, limit = 50) =>
    makeAuthenticatedRequest(
      `/api/admin/v1/unknown-sizes/drilldown?retailer_id=${encodeURIComponent(retailerId)}` +
      `&raw_size=${encodeURIComponent(rawSize)}&limit=${limit}`
    ),
};

// Relationship Overrides (false positive disconnects)
export const relationshipOverridesApi = {
  preview: (productUuidA, productUuidB) =>
    makeAuthenticatedRequest('/api/admin/v1/relationship-overrides/preview', {
      method: 'POST',
      body: JSON.stringify({ product_uuid_a: productUuidA, product_uuid_b: productUuidB }),
    }),

  create: (payload) =>
    makeAuthenticatedRequest('/api/admin/v1/relationship-overrides', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  list: () =>
    makeAuthenticatedRequest('/api/admin/v1/relationship-overrides'),

  restore: (overrideId) =>
    makeAuthenticatedRequest(`/api/admin/v1/relationship-overrides/${overrideId}`, {
      method: 'DELETE',
    }),

  getSignalTags: () =>
    makeAuthenticatedRequest('/api/admin/v1/signal-tags'),

  createSignalTag: (tag) =>
    makeAuthenticatedRequest('/api/admin/v1/signal-tags', {
      method: 'POST',
      body: JSON.stringify({ tag }),
    }),
};

// Retailer API keys (spec dashboard/buyer-insights-api-and-mcp.md §3): issued on request in the CRM
// retailer panel. create() returns the key ONCE under data.key; only its hash is stored.
export const apiKeysApi = {
  list: (retailerId) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/api-keys`),
  create: (retailerId, label) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/api-keys`, {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),
  revoke: (retailerId, keyId) =>
    makeAuthenticatedRequest(
      `/api/admin/v1/retailers/${encodeURIComponent(retailerId)}/api-keys/${encodeURIComponent(keyId)}`,
      { method: 'DELETE' }
    ),
};

// Partner rules (exception engine Phase 4a). Spec:
// .claude-product-specs/overlay/retailer-exception-engine-phase4-controls-PLAN.md §3
const rid = (id) => encodeURIComponent(id);
export const partnerRulesApi = {
  overview: (days = 7) => makeAuthenticatedRequest(`/api/admin/v1/match-exceptions/overview?days=${days}`),
  get: (ruleId, days = 7) => makeAuthenticatedRequest(`/api/admin/v1/match-exceptions/${rid(ruleId)}?days=${days}`),
  create: (rule) =>
    makeAuthenticatedRequest('/api/admin/v1/match-exceptions', { method: 'POST', body: JSON.stringify(rule) }),
  // A change returns the NEW version: its id is new (Q2).
  change: (ruleId, fields) =>
    makeAuthenticatedRequest(`/api/admin/v1/match-exceptions/${rid(ruleId)}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    }),
  stop: (ruleId, reason) =>
    makeAuthenticatedRequest(`/api/admin/v1/match-exceptions/${rid(ruleId)}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  restart: (ruleId) =>
    makeAuthenticatedRequest(`/api/admin/v1/match-exceptions/${rid(ruleId)}/restart`, { method: 'POST' }),
  regions: (retailerId) => makeAuthenticatedRequest(`/api/admin/v1/retailers/${rid(retailerId)}/regions`),
  stores: (retailerId) => makeAuthenticatedRequest(`/api/admin/v1/retailers/${rid(retailerId)}/store-locations`),
  createStore: (retailerId, store) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${rid(retailerId)}/store-locations`, {
      method: 'POST',
      body: JSON.stringify(store),
    }),
  lookupStore: (retailerId, address) =>
    makeAuthenticatedRequest(`/api/admin/v1/retailers/${rid(retailerId)}/store-locations/lookup`, {
      method: 'POST',
      body: JSON.stringify(address),
    }),
  towns: (lat, lon, km) => makeAuthenticatedRequest(`/api/admin/v1/towns?lat=${lat}&lon=${lon}&km=${km}`),
};
